// ─────────────────────────────────────────────────────────────────────────────
// GA4 Types & Interfaces
// Padrão GA4 para itens de e-commerce e parâmetros de eventos.
// ─────────────────────────────────────────────────────────────────────────────

/** Canal de compra — segmentação do funil loja vs Clube Orbitta. */
export type PurchaseOrigin = 'orbitta' | 'clube_orbitta';

export const PURCHASE_ORIGIN_LOJA: PurchaseOrigin = 'orbitta';
export const PURCHASE_ORIGIN_CLUBE: PurchaseOrigin = 'clube_orbitta';

/** Forma de pagamento no checkout do Clube Orbitta (mix pontos + crédito). */
export type ClubePaymentType = 'points' | 'points_credit' | 'credit';

export const CLUBE_PAYMENT_TYPE = {
  POINTS: 'points',
  POINTS_CREDIT: 'points_credit',
  CREDIT: 'credit',
} as const satisfies Record<string, ClubePaymentType>;

/** Opções comuns para eventos de checkout/compra. */
export interface AnalyticsCheckoutOptions {
  coupon?: string;
  purchaseOrigin?: PurchaseOrigin;
}

/**
 * Facetas de catálogo de um item. O payload do carrinho não devolve marca,
 * categoria nem variante — são memorizadas quando o produto passa pelo catálogo
 * e reaplicadas no checkout/purchase para o item chegar completo ao GA4.
 */
export interface GaItemFacets {
  item_brand?: string;
  item_category?: string;
  item_variant?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
}

/** Item no formato GA4 (padrão e-commerce). */
export interface GaItem {
  item_id: string;
  item_name: string;
  item_brand?: string;       // Marca (ex: "CRYSTAL")
  item_category?: string;    // Categoria (ex: "HIDRATAÇÃO")
  item_category2?: string;   // Embalagem (ex: "12 un")
  item_category3?: string;   // Resgatável / Não resgatável
  item_category4?: string;   // Retornável / Não retornável
  item_category5?: string;   // Tags (Combo, Kit, Escalonável, Padrão)
  item_variant?: string;     // Capacidade ou sabor (ex: "290ml", "NATURAL")
  price?: number;
  quantity?: number;
  index?: number;
  discount?: number;
  coupon?: string;
  promotion_id?: string;
  promotion_name?: string;
  creative_slot?: string;
  creative_name?: string;
  item_list_id?: string;
  item_list_name?: string;
  item_origin?: string;
  /**
   * Sistema de recomendação que colocou ESTE item na lista. Dimensão a nível de
   * item (o `recommendation_source` do evento se perde no carrinho, que mistura
   * itens de várias origens) — é o que permite medir no cart/purchase quanto
   * cada versão do recomendador (ex.: _v1A x _v1B em teste A/B) vendeu.
   */
  recommendation_source_item?: string;
}

/**
 * Lista de recomendação de onde o item veio. Memorizada no add_to_cart e
 * reaplicada item a item no checkout/purchase.
 */
export interface SuggestedListContext {
  /** item_list_id (recommended_products | recommended_points_products). */
  id: string;
  /** item_list_name já com o local de exibição (ex.: "Produto Sugerido - Home"). */
  name: string;
  /** Valor de recommendation_source_item. */
  source: string;
}

/** Parâmetros de eventos de promoção (view_promotion / select_promotion). */
export interface GaPromotionParams {
  creative_slot?: string;
  items: GaItem[];
  promo_text?: string;
  promo_img?: string;
  promo_dest?: string;
}

/** Parâmetros comuns de e-commerce (cart, checkout, purchase). */
export interface GaEcommerceParams {
  currency: string;
  value: number;
  items: GaItem[];
  coupon?: string;
}

/** Parâmetros do evento purchase. */
export interface GaPurchaseParams extends GaEcommerceParams {
  transaction_id: string;
  purchase_origin?: string;
  value_paid_reais?: number;
  value_paid_points?: number;
  shipping_tier?: string;
  payment_type?: string;
}

/** Parâmetros do evento add_shipping_info. */
export interface GaShippingParams extends GaEcommerceParams {
  shipping_tier: string;
}

/** Parâmetros do evento add_payment_info. */
export interface GaPaymentParams extends GaEcommerceParams {
  payment_type: string;
}

/** Parâmetros do evento customizado ui_message. */
export interface UiMessageParams {
  msg_type: 'error' | 'warning' | 'info' | 'success';
  msg_code?: string;
  msg_text?: string;
  ui_screen?: string;
  ui_route?: string;
  ui_component?: string;
  ui_action?: string;
}
